import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Loader2, AlertCircle } from "lucide-react";
import axiosInstance from "../../utility/axiosInstance";

// Types
interface Organization {
  id: string;
  name: string;
  contactEmail: string;
  representativeName: string;
  representativeEmail: string;
  representativeAgreementAccepted: boolean;
  createdAt: string;
}

interface Candidate {
  id: string;
  email: string;
  name: string;
  phoneNumber: string;
  createdAt: string;
  updatedAt: string;
  candidateAgreement: boolean;
}

type Agreement = Organization | Candidate;

interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
  error: string | null;
}

interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

type FilterType = "all" | "true" | "false";

type TabType = "organizations" | "candidates";

interface TabConfig {
  id: TabType;
  label: string;
  endpoint: string;
}

// Custom hook for fetching agreements
const useAgreements = (
  endpoint: string,
  filter: FilterType,
  page: number,
  limit: number
) => {
  const [data, setData] = useState<Agreement[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAgreements = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number> = {
        page,
        limit,
      };

      if (filter !== "all") {
        params.agreed = filter;
      }

      const response = await axiosInstance.get<ApiResponse<any>>(endpoint, {
        params,
      });

      // Handle different response structures based on endpoint
      const responseData = response?.data?.data;

      if (responseData) {
        if (
          endpoint.includes("organizations") &&
          Array.isArray(responseData.organizations)
        ) {
          // Transform organizations data to match Agreement type
          const organizations: Agreement[] = responseData.organizations.map(
            (org: Organization) => ({
              ...org,
              // Map organization fields to common Agreement interface
              email: org.representativeEmail,
              agreed: org.representativeAgreementAccepted,
              name: org.representativeName || org.name,
            })
          );
          setData(organizations);

          // Set pagination meta for organizations
          setMeta({
            currentPage: responseData.page || 1,
            totalPages: responseData.totalPages || 1,
            totalItems: responseData.total || 0,
            itemsPerPage: limit,
          });
        } else if (
          endpoint.includes("candidates") &&
          responseData.data &&
          Array.isArray(responseData.data)
        ) {
          // Transform candidates data to match Agreement type
          const candidates: Agreement[] = responseData.data.map(
            (candidate: Candidate) => ({
              ...candidate,
              // Map candidate fields to common Agreement interface
              agreed: candidate.candidateAgreement,
            })
          );
          setData(candidates);

          // Set pagination meta for candidates
          const pagination = responseData.pagination;
          setMeta({
            currentPage: pagination?.page || 1,
            totalPages: pagination?.totalPages || 1,
            totalItems: pagination?.total || 0,
            itemsPerPage: pagination?.limit || limit,
          });
        } else {
          // Fallback if data structure doesn't match expected format
          setData([]);
          setMeta(null);
        }
      } else {
        setData([]);
        setMeta(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setData([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint, filter, page, limit]);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  return { data, meta, loading, error, refetch: fetchAgreements };
};

// Helper function to get agreement status and display name based on type
const getAgreementDisplayInfo = (agreement: Agreement, tabType: TabType) => {
  if (tabType === "organizations") {
    const org = agreement as Organization;
    return {
      name: org.representativeName || org.name || "N/A",
      email: org.representativeEmail || org.contactEmail || "N/A",
      agreed: org.representativeAgreementAccepted,
      createdAt: org.createdAt,
    };
  } else {
    const candidate = agreement as Candidate;
    return {
      name: candidate.name || "N/A",
      email: candidate.email || "N/A",
      agreed: candidate.candidateAgreement,
      createdAt: candidate.createdAt,
    };
  }
};

// Main Component
const AgreementsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("organizations");
  const [filter, setFilter] = useState<FilterType>("all");
  const [page, setPage] = useState(1);
  const limit = 50;

  const tabs: TabConfig[] = [
    {
      id: "organizations",
      label: "Organizations",
      endpoint: "/organizations/by-agreement",
    },
    {
      id: "candidates",
      label: "Candidates",
      endpoint: "/candidates/by-agreement",
    },
  ];

  const activeEndpoint =
    tabs.find((tab) => tab.id === activeTab)?.endpoint || "";

  const { data, meta, loading, error } = useAgreements(
    activeEndpoint,
    filter,
    page,
    limit
  );

  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
    setPage(1);
  };

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          Agreements Management
        </h1>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-[gray]/20">
            <nav className="flex -mb-px">
              {tabs?.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Filters */}
          <div className="p-4 flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Filter:</span>
            <div className="flex gap-2">
              {(["all", "true", "false"] as FilterType[]).map(
                (filterOption) => (
                  <button
                    key={filterOption}
                    onClick={() => handleFilterChange(filterOption)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      filter === filterOption
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-[gray]/20"
                    }`}
                  >
                    {filterOption === "all"
                      ? "All"
                      : filterOption === "true"
                      ? "Agreed"
                      : "Not Agreed"}
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading agreements...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-600">
              <AlertCircle className="w-6 h-6 mr-2" />
              <span>Error: {error}</span>
            </div>
          ) : !data || data.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No agreements found
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-[gray]/20">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                        {activeTab === "organizations"
                          ? "Representative Name"
                          : "Name"}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[gray]/20">
                    {data?.map((agreement) => {
                      const displayInfo = getAgreementDisplayInfo(
                        agreement,
                        activeTab
                      );
                      return (
                        <tr key={agreement?.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {displayInfo.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {displayInfo.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 inline-flex text-sm leading-5 font-medium rounded-full ${
                                displayInfo.agreed
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {displayInfo.agreed ? "Agreed" : "Pending"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(
                              displayInfo.createdAt
                            ).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {meta && meta.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-[gray]/20 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {meta.currentPage} of {meta.totalPages} (
                    {meta.totalItems} total items)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === meta.totalPages}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgreementsPage;
